import { ContentReviewRequest, TrackingUserClaim } from '@tabeeb/contracts/main-api/swagger-main-api'
import React, { useMemo } from 'react'
import {
  Caption,
  Chip,
  DateInfo,
  IconButton,
  OverflowInlineList,
  Stack,
  Subheader3,
  Tooltip,
  useDialogState,
} from '@collaborate-health/uikit'
import { DeleteOutlined, EventOutlined } from '@mui/icons-material'
import CheckInQualification from '@tabeeb/modules/checkIns/components/CheckInsList/CheckInQualification'
import RemoveUserRecordsDialog from '@tabeeb/modules/checkIns/components/RemoveUserRecordsDialog'
import { useSelector } from '@tabeeb/store'
import { getContentId } from '@tabeeb/shared/content/selectors'
import { sessionTitleFormatter } from '@tabeeb/shared/utils/text'
import { ContentCheckInsTabViews } from '@tabeeb/modules/checkIns/components/ContentCheckInsTab/constants'
import { getLatestNotCompletedContentReview } from '@tabeeb/modules/contentReviews/selectors'
import { QualificationsContainer, StyledCard, StyledListItem } from './CheckInListItem.styles'
import { CheckInListItemProps } from './CheckInListItem.types'

const renderClaim = (claim: TrackingUserClaim) => (
  <Tooltip text={`${claim.Type}: ${claim.Value}`}>
    <Chip
      label={
        <Caption textOverflow='ellipsis' whiteSpace='nowrap'>
          {claim.Type}: {claim.Value}
        </Caption>
      }
      color='default'
      variant='outlined'
      size='small'
      maxWidth={150}
    />
  </Tooltip>
)

const CheckInListItem: React.FC<CheckInListItemProps> = ({ user, viewType }) => {
  const [removeUserRecordsDialogOpen, onRemoveUserRecordsDialogOpen, onRemoveUserRecordsDialogClose] = useDialogState()
  const contentId = useSelector<number>(getContentId)

  const latestReview = useSelector<ContentReviewRequest>(getLatestNotCompletedContentReview)

  const isDeleteButtonDisabled = useMemo(() => {
    if (viewType === ContentCheckInsTabViews.OnReview) {
      return true
    }

    if (!latestReview) {
      return false
    }

    return Date.parse(latestReview.CreatedOn) >= Date.parse(user.CreatedOn)
  }, [latestReview, user.CreatedOn, viewType])

  return (
    <StyledListItem disableGutters>
      <StyledCard>
        <Stack direction='row' spacing={1} width='100%' minWidth={0} overflow='hidden'>
          <Stack direction='row' alignItems='flex-start' justifyContent='center' gap={1} flexShrink={0}>
            <EventOutlined color='primary' />
            <DateInfo date={user.CreatedOn} size='medium' variant='stacked' />
          </Stack>
          <Stack direction='column' flex={1} minWidth={0} gap={1}>
            <Stack direction='row' spacing={1} alignItems='center' width='100%' minWidth={0}>
              <Stack direction='column' flex={1} minWidth={0} overflow='hidden'>
                <Subheader3 textOverflow='ellipsis' overflow='hidden' whiteSpace='nowrap'>
                  {user.UserName!.length > 1 ? user.UserName : user.UserEmail}
                </Subheader3>
                <Caption textOverflow='ellipsis' overflow='hidden' whiteSpace='nowrap'>
                  {user.UserEmail}
                </Caption>
              </Stack>
              <Stack flexShrink={0}>
                <Tooltip
                  text={
                    isDeleteButtonDisabled
                      ? sessionTitleFormatter.format(
                          'This record cannot be deleted as it is a part of a session review'
                        )
                      : 'Remove record'
                  }
                >
                  <IconButton
                    size='small'
                    onClick={onRemoveUserRecordsDialogOpen}
                    disabled={isDeleteButtonDisabled}
                    color={isDeleteButtonDisabled ? 'default' : 'error'}
                  >
                    <DeleteOutlined color='inherit' />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            <OverflowInlineList items={user.Claims || []} renderItem={renderClaim} renderHiddenItem={renderClaim} />
            {user.Qualifications?.length ? (
              <QualificationsContainer>
                <OverflowInlineList
                  items={user.Qualifications}
                  direction='column'
                  moreCountPosition='separate'
                  renderItem={(qualificationVersion) => (
                    <CheckInQualification qualificationVersion={qualificationVersion} user={user} />
                  )}
                  renderHiddenItem={(qualificationVersion) => (
                    <CheckInQualification
                      qualificationVersion={qualificationVersion}
                      user={user}
                      renderQualificationDetails={false}
                    />
                  )}
                />
              </QualificationsContainer>
            ) : (
              <Caption mt={1}>No qualifications info</Caption>
            )}
          </Stack>
        </Stack>
      </StyledCard>
      <RemoveUserRecordsDialog
        open={removeUserRecordsDialogOpen}
        onClose={onRemoveUserRecordsDialogClose}
        user={user}
        contentId={contentId}
      />
    </StyledListItem>
  )
}

export default CheckInListItem

